<template>

  <the-overlay :active="state === 'loading'">

    <div id="step2" class="h-split" v-if="state !== 'loading'">

      <div class="container-fluid content">
        <user-details
            :user-form="user"
            :org-data="organization"
            ref="userDetails"
            @update:firstname="user.firstname = $event"
            @update:lastname="user.lastname = $event"
            @update:lang="user.preferredLanguage = $event"
            @update:roles="user.roles = $event"
            @update:organization="onOrganizationSelect"
            @update:admin-level="user.adminLevel = $event"
            @update:birthdate="user.birthdate = toISODate($event)"
            @update:managed-organizations="user.managedOrganizations = $event"/>
      </div>

      <licenses-editor
          ref="licenseEditor"
          :org-licenses="organization.licenses"
          :user-licenses="user.licenses"
          :license-definitions="organizationLicenses"
          @update-selection="updateLicenses"
          @save-changes="saveChanges">
        <input
            id="btnSave"
            type="button"
            class="btn btn-primary w-100"
            :value="$t('adm.button.save')"
            @click="saveChanges"
            @keyup.enter="saveChanges"/>
      </licenses-editor>
    </div>

  </the-overlay>

</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import TheOverlay from '@/components/ui/TheOverlay.vue';
import Backend, {
  BackendError,
  BusinessType,
  CreateUserRequest,
  LicenseData, OrganizationDetails, QueryOptions,
} from '@/components/backend';
import { i18n, toSupportedLocale } from '@/lang';
import { Logger } from 'oidc-client-ts';
import { UserForm } from '@/views/Users/edit/index.vue';
import LicensesEditor from '@/views/Users/edit/LicensesEditor.vue';
import UserDetails from '@/views/Users/edit/UserDetails.vue';
import { isStihlEmployee, toISODate } from '@/components/utils';

export default defineComponent({
  name: 'AddStep2',
  components: {
    LicensesEditor, UserDetails, TheOverlay,
  },
  props: {
    userForm: { type: Object as PropType<UserForm>, required: true },
  },
  data() {
    return {
      state: 'loading' as 'loading' | 'ready' | 'err',
      user: {
        ...this.userForm,
        adminLevel: 'none',
        managedOrganizations: [],
        preferredLanguage: toSupportedLocale(this.userForm.preferredLanguage, i18n.global.locale),
      } as UserForm,
      organization: {} as OrganizationDetails,
      organizationLicenses: [] as LicenseData[],
    };
  },
  computed: {},
  methods: {
    toISODate,

    compUserDetails(): typeof UserDetails {
      return this.$refs.userDetails as typeof UserDetails;
    },

    compLicenseEditor(): typeof LicensesEditor {
      return this.$refs.licenseEditor as typeof LicensesEditor;
    },

    gotoUser(uid: string) {
      this.$router.push(`/${i18n.global.locale}/members/${uid}`);
    },

    onOrganizationSelect(oid: string) {
      this.user.organization = oid;
      this.loadOrganization(oid);
    },

    updateLicenses(list: string[]) {
      // Logger.debug("AddUser:2","newLicenses:", list);
      this.user.licenses = list;
    },

    async saveChanges() {
      Logger.debug('AddUser:2', 'saving changes...');
      this.$toast.clear();

      if (!this.compUserDetails()?.isValid()) return;

      const createReq = { ...this.user } as CreateUserRequest;

      if (isStihlEmployee(this.user.email) || this.organization.businessType === BusinessType.Subsidiary) {
        createReq.syncFlag = false; // WEBSSO-1065
      } else {
        // if the crm-id was set or user belongs to the auto-sync countries set the synchronization flag
        const auto4countries = window.APP_CONFIG.sync.auto4countries as string[];
        if (!!this.user.crmId || !!auto4countries.find((c) => this.organization.countries?.indexOf(c) !== -1)) {
          createReq.syncFlag = true;
        }
      }

      if (!createReq.pin?.value) {
        createReq.pin = undefined; // if no PIN value was set - remove it
      } else {
        this.user.pin.type = 'CPF'; // todo check country === 'BRA'
      }

      this.state = 'loading';
      Backend.validateToken().then((api) => api
          .createUser(createReq)
          .then(() => {
            Logger.debug('AddUser:2', 'saved.');

            // this.$router.back();
            this.$toast.success(this.$t('adm.page.addUser.infoSuccess'), { duration: 5000 });
            this.goBack1Page();

            // this.loadUserData(this.$route.params.id as string)
          })
          .catch(this.handleError));
    },

    handleError(e: BackendError) {
      if (e.response?.status === 400 && e.restError?.code === 'INVALID_PARAMETER' && e.restError?.invalidField === 'birthdate') {
        this.$toast.error(this.$t('adm.error.invalidBirthDate'));
      } else {
        this.$toast.error(Backend.errorMessage(e, this.$t));
      }
      this.state = 'err';
    },

    loadOrganization(oid: string) {
      this.state = 'loading';
      Backend.validateToken().then((api) => api
          .loadOrganization(oid, { $expand: 'licenses,admins', $locale: i18n.global.locale } as QueryOptions)
          .then((r1) => {
            Logger.debug('AddUser:2', 'org loaded');
            this.organization = r1.data;
            this.organizationLicenses = r1.extra?.licenses ?? [];

            this.state = 'ready';
          })
          .catch(this.handleError));
    },

  },
  mounted() {
    Logger.debug('AddUser:2', 'mounted', this.userForm);
    this.loadOrganization(this.userForm.organization as string);
  },
});
</script>

<style>
</style>
